import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import CapaEffectivenessDetailsTab from './components/tabs/CapaEffectivenessDetailsTab';
import CapaEffectivenessAuditLogTab from './components/tabs/CapaEffectivenessAuditLogTab';
import { useFlexibleList } from '../../hooks/useSWR';
import { DocumentTextIcon, ClipboardCheckIcon, XIcon } from '../../components/ui/Icons';

/**
 * Custom Split View for CAPA Effectiveness Check: Details + Audit Log.
 */
const CustomCapaEffectivenessSplitView = ({
  isOpen,
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  labels,
  onClose,
  onRecordUpdate,
}) => {
  const { theme } = useTheme();

  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id
      ? `/audit-log/master/${config?.features?.masterEntityTypeId || 20}/${selectedRecord.id}`
      : null,
  );

  const { data: fetchedFileAttachments = [] } = useFlexibleList(
    config?.features?.fileAttachments && selectedRecord?.id
      ? `/file-attachment/entity/${config?.entityTypeId || 20}/${selectedRecord.id}`
      : null,
  );

  const fileAttachments = (
    fetchedFileAttachments.length > 0
      ? fetchedFileAttachments
      : selectedRecordFiles || []
  ).filter((file) => !file.isDeleted);

  const allTabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge:
        config?.features?.fileAttachments && fileAttachments.length > 0
          ? fileAttachments.length
          : null,
      badgeColor:
        config?.features?.fileAttachments && fileAttachments.length > 0
          ? 'bg-blue-600'
          : null,
    },
    {
      value: 'audit-log',
      label: 'Audit Log',
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: 'bg-red-600',
    },
  ];

  if (!isOpen) return null;

  return (
    <div
      className={cn('hidden lg:block min-h-0 h-full pb-2')}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div
        className={cn(
          'min-h-0 h-full rounded-xl border flex flex-col',
          theme.border.light,
          theme.colors.surface,
          theme.shadow.md,
        )}
      >
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn('text-sm font-semibold', theme.text.primary)}>
            {selectedRecord
              ? `CAPA Effectiveness - ${selectedRecord.code || selectedRecord.id}`
              : 'Select a CAPA effectiveness check to view details'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1 rounded hover:opacity-80', theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />

            <div className="flex-1 flex flex-col min-h-0">
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <CapaEffectivenessDetailsTab
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={fileAttachments}
                  config={config}
                  fields={fields}
                  onRecordUpdate={onRecordUpdate}
                />
              </TabsContent>

              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <CapaEffectivenessAuditLogTab
                  selectedRecord={selectedRecord}
                  config={config}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn('p-4 text-sm', theme.text.secondary)}>
            {labels.noSelectionText || 'No CAPA effectiveness check selected'}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomCapaEffectivenessSplitView;
